import {
  useState,
  useRef,
  useEffect,
  useCallback,
  SyntheticEvent,
} from "react";
import { Grid } from "@giphy/react-components";
import { GiphyFetch } from "@giphy/js-fetch-api";
import { IGif } from "@giphy/js-types";
import styled from "styled-components";
import { HiOutlineGif } from "react-icons/hi2";
import { Box } from "rebass";

type GifPosition = "top" | "bottom" | "left" | "right";

interface IGifSelectorProps {
  onGifSelect: (gifUrl: string) => void;
  position?: GifPosition;
}

const POPOVER_WIDTH = 320;
const GRID_WIDTH = POPOVER_WIDTH - 24;
const SEARCH_DEBOUNCE_MS = 400;

const apiKey = process.env.REACT_APP_GIPHY_API_KEY;
const giphyFetch = apiKey ? new GiphyFetch(apiKey) : null;

export const GifSelector = ({
  onGifSelect,
  position = "top",
}: IGifSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const fetchGifs = useCallback(
    (offset: number) => {
      if (!giphyFetch) return Promise.reject(new Error("Giphy not configured"));
      return debouncedSearch
        ? giphyFetch.search(debouncedSearch, { offset, limit: 12 })
        : giphyFetch.trending({ offset, limit: 12 });
    },
    [debouncedSearch],
  );

  const handleGifClick = (gif: IGif, e: SyntheticEvent) => {
    e.preventDefault();
    onGifSelect(gif.images.original.url);
    setIsOpen(false);
    setSearch("");
    setDebouncedSearch("");
  };

  if (!giphyFetch) return null;

  return (
    <GifSelectorWrapper ref={containerRef}>
      <GifButton
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-label="Add a GIF"
      >
        <HiOutlineGif size={20} />
      </GifButton>
      {isOpen && (
        <GifPopover position={position}>
          <SearchInput
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search GIPHY"
          />
          <GridScroll>
            <Grid
              key={debouncedSearch}
              fetchGifs={fetchGifs}
              width={GRID_WIDTH}
              columns={3}
              gutter={6}
              hideAttribution
              noLink
              onGifClick={handleGifClick}
            />
          </GridScroll>
          <Attribution>Powered by GIPHY</Attribution>
        </GifPopover>
      )}
    </GifSelectorWrapper>
  );
};

const GifSelectorWrapper = styled.div`
  display: inline-block;
  overflow: visible;
  position: relative;
`;

const GifButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 99px;
  color: ${({ theme }) => theme.colors.shade300};
  cursor: pointer;
  display: flex;
  height: 22px;
  justify-content: center;
  padding: 0;
  transition: color 0.2s ease-in-out;
  width: 22px;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const GifPopover = styled(Box)<{ position: GifPosition }>`
  animation: fadeIn 0.2s ease-in-out;
  background-color: ${({ theme }) => theme.colors.ned};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.25);
  padding: 12px;
  position: absolute;
  width: ${POPOVER_WIDTH}px;
  z-index: 9999;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-5px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  ${({ position }) => {
    switch (position) {
      case "bottom":
        return `
          margin-top: 8px;
          right: 0;
          top: 100%;
        `;
      case "left":
        return `
          margin-right: 8px;
          right: 100%;
          top: 0;
        `;
      case "right":
        return `
          left: 100%;
          margin-left: 8px;
          top: 0;
        `;
      case "top":
      default:
        return `
          bottom: 100%;
          margin-bottom: 8px;
          right: 0;
        `;
    }
  }}

  @media (max-width: 768px) {
    transform: translateX(40px);
  }
`;

const SearchInput = styled.input`
  background-color: ${({ theme }) => theme.colors.navy500};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  margin-bottom: 10px;
  outline: none;
  padding: 8px 10px;
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => theme.colors.chalk};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

const GridScroll = styled(Box)`
  height: 280px;
  overflow-x: hidden;
  overflow-y: auto;

  .giphy-gif,
  .giphy-gif-img {
    cursor: pointer;
  }
`;

const Attribution = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 10px;
  letter-spacing: 0.04em;
  margin-top: 8px;
  text-align: right;
  text-transform: uppercase;
`;
